import { useMemo, useState, useEffect } from 'react';

// Utils
import cn from '../../../../utils/cn';
import groupOptionsByOptGroup from '../../../../utils/optGroup/optGroup';

// Styles
import styles from './SelectClassField.module.css';

// Components
import SelectField from '../../../atoms/Select/Select';

// Queries
import { useSelectOptions } from '../../../../services/table-filters/tableFiltersQueries';

/**
 * BatchActionEnumInput - Simple select for enum values in batch actions (based on BatchActionSelect but without groups)
 * @param {object} props
 * @param {string | null} [props.value] - Current selected value
 * @param {(value: string | null, option: import('react-select').SingleValue<{ value: string, label: string, color?: string | null }> | null) => void} props.onChange - Change handler. The second arg is the react-select option (or null if cleared); consumers that only care about the value can ignore it (e.g. react-hook-form Controller).
 * @param {(e: React.FocusEvent<HTMLInputElement>) => void} [props.onBlur] - Blur handler
 * @param {string} [props.name] - Input name attribute
 * @param {boolean} [props.disabled] - Whether input is disabled
 * @param {boolean} [props.isReadOnly] - Locked; value remains in form
 * @param {string} [props.error] - Validation error
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.theme] - Theme of the select field
 * @param {string} [props.selectOptionsUrl] - URL to fetch the select options
 * @param {boolean} [props.initialOpen] - Whether to open the menu initially (for direct edit)
 * @param {() => void} [props.onEscapeWhenMenuClosed] - When set (e.g. table direct edit), Escape closes the menu if needed and exits edit in one keypress
 * @returns {JSX.Element} BatchActionEnumInput component
 */
function SelectClassField({
  selectOptionsUrl = '',
  theme = 'default',
  value = null,
  onChange,
  onBlur,
  onEscapeWhenMenuClosed,
  name,
  disabled = false,
  isReadOnly = false,
  error = null,
  className = '',
  placeholder = '',
  initialOpen = false,
}) {
  const { data: options, isLoading } = useSelectOptions(selectOptionsUrl);
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  // Open the menu when initialOpen becomes true. Using of initialOpen because in order to open the menu from without we need to control it via this prop
  useEffect(() => {
    if (initialOpen) {
      setMenuIsOpen(true);
    }
  }, [initialOpen]);

  const handleSelectChange = (
    /** @type {import('react-select').SingleValue<{ value: string, label: string, color?: string | null }>} */ selectedOption,
  ) => {
    const newValue = selectedOption?.value;
    if (newValue === '' || newValue === undefined) {
      onChange(null, null);
      return;
    }
    onChange(newValue, selectedOption ?? null);
  };

  const optionsOptGrouped = useMemo(() => {
    if (isLoading || !options?.length) return [];
    return groupOptionsByOptGroup(options).map((group) => ({
      label: group.lib,
      options: group.options.map((option) => ({
        value: option.id,
        label: option.lib,
        color: option.color,
      })),
    }));
  }, [options, isLoading]);

  /**
   * Table direct edit: one Escape closes the menu (if open) and exits edit.
   * @param {React.KeyboardEvent<HTMLDivElement>} e
   */
  const handleSelectKeyDown = (e) => {
    if (!onEscapeWhenMenuClosed) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      setMenuIsOpen(false);
      onEscapeWhenMenuClosed();
    }
  };

  return (
    <div className={cn([styles.wrapper, className])}>
      <div className={cn([
        styles.group,
        disabled && styles['enum-disabled'],
        isReadOnly && styles['enum-read-only'],
      ])}
      >
        <SelectField
          id={name}
          theme={theme}
          value={value}
          options={optionsOptGrouped}
          placeholder={placeholder}
          onChange={(selectedOption) => handleSelectChange(selectedOption)}
          onBlur={onBlur}
          className={styles['enum-full-width']}
          disabled={disabled}
          isReadOnly={isReadOnly}
          isLoading={isLoading}
          menuIsOpen={menuIsOpen}
          onMenuOpen={() => setMenuIsOpen(true)}
          onMenuClose={() => setMenuIsOpen(false)}
          escapeClearsValue={onEscapeWhenMenuClosed ? false : undefined}
          onKeyDown={handleSelectKeyDown}
        />
      </div>

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

export default SelectClassField;
